// YtScanViewer.js — Dark Pattern Review Tool
// Detailed view for a single YT scan.
// Left panel (60%): YouTube embed player.
// Right panel (40%): All Dark pattern cards stacked + internal feedback.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchYtScan, updateYtPattern, deleteYtPattern, updateYtFeedback, addYtPattern } from "../api";
import ClientHeader from "./ClientHeader";

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (sec) => {
    if (sec == null) return "";
    const s = Math.floor(sec);
    const m = Math.floor(s / 60);
    const ss = s % 60;
    return `${m}:${ss.toString().padStart(2, "0")}`;
};



// ── Sub-components ────────────────────────────────────────────────────────────

const PatternCard = ({ pattern, scanId, isSelected, onClick, onSaved, onDeleted }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        type: pattern.dark_pattern_type || "",
        desc: pattern.dark_pattern_description || "",
        category: pattern.category || "",
        classification: pattern.classification || "",
        player_impact: pattern.player_impact || "",
        evidence: pattern.evidence || "",
        ui_indicators: pattern.ui_indicators || "",
        timestamp_start: pattern.timestamp_start || 0,
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    

    const handleSave = async (e) => {
        e.stopPropagation();
        setSaving(true);
        try {
            const updated = await updateYtPattern(scanId, pattern.pattern_id, {
                dark_pattern_type: draft.type,
                dark_pattern_description: draft.desc,
                category: draft.category,
                classification: draft.classification,
                player_impact: draft.player_impact,
                evidence: draft.evidence,
                ui_indicators: draft.ui_indicators,
                timestamp_start: parseFloat(draft.timestamp_start) || 0,
            });
            onSaved(updated);
            setEditing(false);
        } catch (e) {
            console.error("Save failed", e);
            alert("Failed to save pattern updates.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (e) => {
        e.stopPropagation();
        if (!window.confirm("Are you sure you want to permanently remove this compliance issue?")) return;
        setDeleting(true);
        try {
            await deleteYtPattern(scanId, pattern.pattern_id);
            onDeleted(pattern.pattern_id);
        } catch (e) {
            console.error("Delete failed", e);
            alert("Failed to delete issue.");
            setDeleting(false);
        }
    };

    return (
        <div
            onClick={!editing ? onClick : undefined}
            style={{
                border: isSelected ? `2px solid var(--accent)` : `1px solid var(--border)`,
                borderRadius: 12,
                padding: "16px",
                marginBottom: 16,
                background: isSelected ? "rgba(145,128,253,0.04)" : "#fff",
                cursor: !editing ? "pointer" : "default",
                transition: "all 0.15s ease",
                boxShadow: isSelected ? "0 2px 12px rgba(145,128,253,0.15)" : "0 1px 3px rgba(0,0,0,0.04)",
                opacity: deleting ? 0.5 : 1,
                pointerEvents: deleting ? "none" : "auto"
            }}
        >
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                {editing ? (
                    <>
                        
                        <input
                            value={draft.category}
                            onChange={e => setDraft(p => ({ ...p, category: e.target.value }))}
                            onClick={e => e.stopPropagation()}
                            placeholder="Category"
                            style={{
                                width: "100px", border: "1.5px solid var(--border)", borderRadius: 6, padding: "5px 8px",
                                fontSize: 13, outline: "none", background: "var(--white)",
                            }}
                        />
                        <input
                            value={draft.classification}
                            onChange={e => setDraft(p => ({ ...p, classification: e.target.value }))}
                            onClick={e => e.stopPropagation()}
                            placeholder="Classification"
                            style={{
                                width: "100px", border: "1.5px solid var(--border)", borderRadius: 6, padding: "5px 8px",
                                fontSize: 13, outline: "none", background: "var(--white)",
                            }}
                        />
                    </>
                ) : (
                    <>
                        <span style={{
                            background: "#f1f3f4", color: "#3c4043",
                            fontSize: 11, fontWeight: 700, padding: "4px 10px",
                            borderRadius: 6, border: "1px solid #dadce0"
                        }}>
                            {pattern.classification || "Unclassified"}
                        </span>
                        <span style={{
                                background: "#E8EAED", color: "#202124",
                                fontSize: 11, fontWeight: 700, padding: "4px 10px",
                                borderRadius: 6, border: "1px solid #DADCE0"
                            }}>
                                {pattern.category || "Uncategorized"}
                            </span>
                    </>
                )}

                {pattern.timestamp_start != null && (
                    <span style={{
                        marginLeft: "auto", fontSize: 12, color: "var(--text-mid)",
                        background: "#F8F9FA", padding: "4px 10px", borderRadius: 6,
                        fontWeight: 600, border: "1px solid var(--border)",
                    }}>
                        ▶ {fmt(pattern.timestamp_start)}{pattern.timestamp_end ? ` → ${fmt(pattern.timestamp_end)}` : ""}
                    </span>
                )}
            </div>

            {/* Description */}
            {editing ? (
                <textarea
                    value={draft.desc}
                    onChange={e => setDraft(p => ({ ...p, desc: e.target.value }))}
                    onClick={e => e.stopPropagation()}
                    rows={3}
                    placeholder="Description"
                    style={{
                        width: "100%", border: "1.5px solid var(--border)", borderRadius: 8,
                        padding: "8px 10px", fontSize: 13, resize: "vertical", outline: "none",
                        fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box", marginBottom: 8,
                    }}
                />
            ) : (
                <p style={{ fontSize: 14, color: "var(--text-dark)", lineHeight: 1.6, margin: "0 0 12px" }}>
                    {pattern.dark_pattern_description}
                </p>
            )}

            {/* Evidence */}
            {editing ? (
                <>
                <textarea
                    value={draft.evidence}
                    onChange={e => setDraft(p => ({ ...p, evidence: e.target.value }))}
                    onClick={e => e.stopPropagation()}
                    rows={2}
                    placeholder='Evidence: "..."'
                    style={{
                        width: "100%", border: "1.5px solid var(--border)", borderRadius: 8,
                        padding: "8px 10px", fontSize: 13, resize: "vertical", outline: "none",
                        fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box", marginBottom: 8,
                    }}
                />
                <textarea
                    value={draft.player_impact}
                    onChange={e => setDraft(p => ({ ...p, player_impact: e.target.value }))}
                    onClick={e => e.stopPropagation()}
                    rows={2}
                    placeholder="Player Impact"
                    style={{
                        width: "100%", border: "1.5px solid var(--border)", borderRadius: 8,
                        padding: "8px 10px", fontSize: 13, resize: "vertical", outline: "none",
                        fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box", marginBottom: 8,
                    }}
                />
                </>
            ) : (
                <>
                {pattern.evidence && (
                    <div style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-mid)" }}>
                        <strong>Evidence:</strong> "{pattern.evidence}"
                    </div>
                )}
                {pattern.player_impact && (
                    <div style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-mid)" }}>
                        <strong>Player Impact:</strong> "{pattern.player_impact}"
                    </div>
                )}
                </>
            )}

            {/* UI Indicators & Confidence */}
            {editing ? (
                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 2 }}>
                        <label style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, display: "block", marginBottom: 4 }}>UI Indicators</label>
                        <input
                            value={draft.ui_indicators}
                            onChange={e => setDraft(p => ({ ...p, ui_indicators: e.target.value }))}
                            onClick={e => e.stopPropagation()}
                            placeholder="UI Indicators"
                            style={{
                                width: "100%", border: "1.5px solid var(--border)", borderRadius: 6,
                                padding: "6px 8px", fontSize: 12, outline: "none", boxSizing: "border-box"
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, display: "block", marginBottom: 4 }}>Timestamp (s)</label>
                        <input
                            type="number"
                            value={draft.timestamp_start}
                            onChange={e => setDraft(p => ({ ...p, timestamp_start: e.target.value }))}
                            onClick={e => e.stopPropagation()}
                            style={{
                                width: "100%", border: "1.5px solid var(--border)", borderRadius: 6,
                                padding: "6px 8px", fontSize: 12, outline: "none", boxSizing: "border-box"
                            }}
                        />
                    </div>
                </div>
            ) : (
                <>
                    {pattern.ui_indicators && (
                        <p style={{ fontSize: 12, color: "var(--text-mid)", margin: "0 0 8px" }}>
                            🖥 <strong>UI:</strong> {pattern.ui_indicators}
                        </p>
                    )}
                </>
            )}

            {/* Footer row (Confidence & Buttons) */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: editing ? 0 : 12, borderTop: editing ? "none" : "1px solid var(--border)", paddingTop: editing ? 0 : 12 }}>
                {!editing && pattern.classification && (
                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        Class: <strong style={{ color: "var(--text-dark)" }}>{pattern.classification}</strong>
                    </span>
                )}
                {editing && <div></div>}

                {/* Edit / Save / Delete buttons */}
                <div style={{ display: "flex", gap: 8 }} onClick={e => e.stopPropagation()}>
                    {editing ? (
                        <>
                            <button
                                onClick={(e) => { e.stopPropagation(); setDraft({
                                    type: pattern.dark_pattern_type || "",
                                    desc: pattern.dark_pattern_description || "",
                                    category: pattern.category || "",
                                    classification: pattern.classification || "",
                                    player_impact: pattern.player_impact || "",
                                    evidence: pattern.evidence || "",
                                    ui_indicators: pattern.ui_indicators || "",
                                    timestamp_start: pattern.timestamp_start || 0,
                                }); setEditing(false); }}
                                style={{
                                    background: "#F1F3F4", color: "var(--text-mid)", border: "none",
                                    borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                style={{
                                    background: "var(--accent)", color: "#fff", border: "none",
                                    borderRadius: 6, padding: "6px 16px", fontSize: 12, cursor: "pointer", fontWeight: 600
                                }}
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                                style={{
                                    background: "transparent", border: "1px solid var(--border)",
                                    borderRadius: 6, padding: "5px 12px", cursor: "pointer",
                                    color: "var(--text-mid)", fontSize: 12, fontWeight: 600
                                }}
                            >
                                Edit
                            </button>
                            <button
                                onClick={handleDelete}
                                style={{
                                    background: "transparent", border: "1px solid #FECACA",
                                    borderRadius: 6, padding: "5px 12px", cursor: "pointer",
                                    color: "#DC2626", fontSize: 12, fontWeight: 600
                                }}
                            >
                                Remove
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};


const NotesPanel = ({ scanId, initialFeedback }) => {
    const [feedback, setFeedback] = useState(initialFeedback || "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        setFeedback(initialFeedback || "");
    }, [initialFeedback]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateYtFeedback(scanId, feedback);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) { 
            console.error(e);
            alert("Failed to save feedback.");
        }
        finally { setSaving(false); }
    };

    return (
        <div style={{
            background: "#fff", border: "1px solid var(--border)",
            borderRadius: 12, padding: "16px", marginTop: 24, marginBottom: 24,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
        }}>
            <div style={{
                fontSize: 12, fontWeight: 700, color: "var(--text-muted)",
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12,
            }}>
                Internal Feedback
            </div>
            <textarea
                value={feedback}
                onChange={e => setFeedback(e.target.value)}
                rows={4}
                placeholder="Add internal notes, observations, or context about this scan..."
                style={{
                    width: "100%", border: "1px solid var(--border)", borderRadius: 8,
                    padding: "12px", fontSize: 14, resize: "vertical", outline: "none",
                    fontFamily: "inherit", lineHeight: 1.6, boxSizing: "border-box",
                    color: "var(--text-dark)", marginBottom: 12,
                }}
                onFocus={e => e.target.style.borderColor = "var(--accent)"}
                onBlur={e => e.target.style.borderColor = "var(--border)"}
            />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
                {saved && !saving && <span style={{ color: "#137333", fontSize: 13, fontWeight: 600 }}>✓ Saved successfully</span>}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                        background: "var(--accent)", color: "#fff", border: "none",
                        borderRadius: 6, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 600,
                        opacity: saving ? 0.7 : 1
                    }}
                >
                    {saving ? "Saving..." : "Save Feedback"}
                </button>
            </div>
        </div>
    );
};


// ── Main Component ─────────────────────────────────────────────────────────────

const YtScanViewer = ({ user, onLogout }) => {
    const { scanId } = useParams();
    const navigate = useNavigate();

    const [scan, setScan] = useState(null);
    const [patterns, setPatterns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [selectedPatternId, setSelectedPatternId] = useState(null);
    const [currentVideoTime, setCurrentVideoTime] = useState(0);

    // Resizable right panel
    const [rightPanelWidth, setRightPanelWidth] = useState(550);
    const [isResizing, setIsResizing] = useState(false);
    const containerRef = useRef(null);

    // Load
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const scanData = await fetchYtScan(scanId);
                setScan(scanData);
                const fetchedPatterns = scanData.patterns || [];
                setPatterns(fetchedPatterns);
                if (fetchedPatterns.length > 0) {
                    setSelectedPatternId(fetchedPatterns[0].pattern_id);
                    setCurrentVideoTime(fetchedPatterns[0].timestamp_start || 0);
                }
            } catch (err) {
                setError(err.message || "Failed to load scan.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [scanId]);

    // Resize handler
    const handleMouseMove = useCallback((e) => {
        if (!isResizing || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const newRight = rect.right - e.clientX;
        setRightPanelWidth(Math.max(380, Math.min(800, newRight)));
    }, [isResizing]);

    const handleMouseUp = useCallback(() => setIsResizing(false), []);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Handlers
    const handlePatternSaved = (updated) => {
        setPatterns(prev => prev.map(p =>
            p.pattern_id === updated.pattern_id ? { ...p, ...updated } : p
        ));
    };

    const handlePatternDeleted = (deletedId) => {
        setPatterns(prev => prev.filter(p => p.pattern_id !== deletedId));
    };

    const handleAddPattern = async () => {
        try {
            const newPat = await addYtPattern(scanId);
            setPatterns(prev => [newPat, ...prev]);
            setSelectedPatternId(newPat.pattern_id);
        } catch (e) {
            console.error(e);
            alert("Failed to add new issue.");
        }
    };

    const handlePatternClick = (p) => {
        setSelectedPatternId(p.pattern_id);
        if (p.timestamp_start != null) {
            setCurrentVideoTime(p.timestamp_start);
        }
    };

    const videoSrc = scan
        ? `https://www.youtube.com/embed/${scan.video_id}?start=${Math.floor(currentVideoTime)}&autoplay=0`
        : null;

    if (loading) return (
        <div className="sessions-page-container">
            <ClientHeader user={user} onLogout={onLogout} />
            <div className="cr-page"><div className="cr-loading">Loading scan…</div></div>
        </div>
    );

    if (error) return (
        <div className="sessions-page-container">
            <ClientHeader user={user} onLogout={onLogout} />
            <div className="cr-page">
                <div className="cr-alert cr-alert-error">{error}</div>
                <button className="cr-btn" onClick={() => navigate("/videos")} style={{ marginTop: 16 }}>← Back to Videos</button>
            </div>
        </div>
    );

    return (
        <div className="sessions-page-container" style={{ height: "100vh", overflow: "hidden" }}>
            <ClientHeader user={user} onLogout={onLogout} />

            {/* Top bar */}
            <div style={{
                display: "flex", alignItems: "center", gap: 16, padding: "12px 24px",
                background: "var(--white)", borderBottom: "1px solid var(--border)", flexShrink: 0,
            }}>
                <button
                    onClick={() => navigate("/videos")}
                    style={{
                        background: "none", border: "1px solid var(--border)", borderRadius: 8,
                        padding: "6px 14px", cursor: "pointer", fontSize: 13, color: "var(--text-mid)",
                        display: "flex", alignItems: "center", gap: 6, fontWeight: 600
                    }}
                >
                    ← Back
                </button>

                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-dark)" }}>
                        {scan?.video_title || scan?.video_id}
                    </div>
                    {scan?.channel_title && (
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{scan.channel_title}</div>
                    )}
                </div>

                {/* Scan meta */}
                <div style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "right" }}>
                    <div><strong style={{ color: "var(--text-dark)" }}>{patterns.length}</strong> issues</div>
                    {scan?.confidence_score != null && (
                        <div>{Math.round(scan.confidence_score * 100)}% confidence</div>
                    )}
                </div>
            </div>

            {/* ── Split panel ── */}
            <div
                ref={containerRef}
                style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}
            >
                {/* Left: YouTube embed */}
                <div style={{ flex: 1, background: "#000", display: "flex", flexDirection: "column", minWidth: 0 }}>
                    {videoSrc && (
                        <iframe
                            key={videoSrc}
                            src={videoSrc}
                            title="YouTube video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    )}
                </div>

                {/* Drag handle */}
                <div
                    onMouseDown={() => setIsResizing(true)}
                    style={{
                        width: 8, background: "#f1f3f4", cursor: "col-resize", flexShrink: 0,
                        borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)",
                        transition: "background 0.15s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#e8eaed"}
                    onMouseLeave={e => e.currentTarget.style.background = "#f1f3f4"}
                />

                {/* Right: Detailed Review (Compliance Issues + Feedback) */}
                <div style={{
                    width: rightPanelWidth, flexShrink: 0,
                    display: "flex", flexDirection: "column",
                    background: "var(--bg)", overflowY: "auto",
                    padding: "24px 20px"
                }}>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-dark)" }}>
                            Compliance Issues
                        </div>
                        <button 
                            onClick={handleAddPattern}
                            style={{
                                background: "#f1f3f4", color: "var(--text-dark)", border: "1px solid var(--border)",
                                borderRadius: 6, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600
                            }}
                        >
                            + Add Issue
                        </button>
                    </div>

                    {patterns.length === 0 ? (
                        <div className="cr-empty">No compliance issues found.</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            {patterns.map((p) => (
                                <PatternCard
                                    key={p.pattern_id}
                                    pattern={p}
                                    scanId={scanId}
                                    isSelected={p.pattern_id === selectedPatternId}
                                    onClick={() => handlePatternClick(p)}
                                    onSaved={handlePatternSaved}
                                    onDeleted={handlePatternDeleted}
                                />
                            ))}
                        </div>
                    )}

                    <NotesPanel scanId={scanId} initialFeedback={scan?.internal_feedback} />
                </div>
            </div>
        </div>
    );
};

export default YtScanViewer;